import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import { apiClient, API_URL } from './config';


// ─── Resume merge helpers ────────────────────────────────────────────────────
// When a user re-uploads a resume we must NOT wipe out manually-added pool
// entries (extra projects, education, work experience) or user-entered scalar
// values that the LLM simply didn't find on the new resume.

/**
 * Returns true if the value is considered "empty" (null, "", [], or an array
 * whose every item is blank / an all-blank object).
 */
const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) {
        if (value.length === 0) return true;
        return value.every(item => {
            if (typeof item === 'string') return item.trim() === '';
            if (item && typeof item === 'object')
                return Object.values(item).every(v => isEmptyValue(v));
            return true;
        });
    }
    if (typeof value === 'object') {
        return Object.values(value).every(v => isEmptyValue(v));
    }
    return false;
};

/**
 * Merge two arrays of objects using `keyField` as the deduplication key.
 * - All non-blank existing items are preserved (project pool).
 * - Incoming items that match an existing item (by keyField, case-insensitive)
 *   update that existing item with fresh data from the resume.
 * - Incoming items with no match are appended.
 * Falls back to `incoming` if existing is empty, and to `existing` if incoming
 * has no real data.
 */
const mergeItemPool = (existing, incoming, keyField) => {
    const hasReal = (arr) =>
        Array.isArray(arr) && arr.some(
            item => item && typeof item === 'object' &&
                Object.values(item).some(v => v && String(v).trim() !== '')
        );

    if (!hasReal(incoming)) return hasReal(existing) ? existing : null;
    if (!hasReal(existing)) return incoming;

    const nonEmpty = existing.filter(
        item => item && typeof item === 'object' &&
            Object.values(item).some(v => v && String(v).trim() !== '')
    );
    const result = [...nonEmpty];

    for (const newItem of incoming) {
        if (!newItem || !Object.values(newItem).some(v => v && String(v).trim() !== '')) continue;
        const newKey = String(newItem[keyField] || '').trim().toLowerCase();
        const matchIdx = newKey
            ? result.findIndex(ex => String(ex[keyField] || '').trim().toLowerCase() === newKey)
            : -1;
        if (matchIdx >= 0) {
            result[matchIdx] = newItem;
        } else {
            result.push(newItem);
        }
    }
    return result.length > 0 ? result : incoming;
};

/**
 * Merge two skills objects by unioning each sub-array (case-insensitive dedup).
 * If `incoming` is empty/null, keeps `existing`. Otherwise unions each category.
 */
const mergeSkills = (existing, incoming) => {
    const hasRealSkills = (s) =>
        s && typeof s === 'object' &&
        Object.values(s).some(a => Array.isArray(a) && a.some(v => v?.trim()));

    if (!hasRealSkills(incoming)) return hasRealSkills(existing) ? existing : incoming;
    if (!hasRealSkills(existing)) return incoming;

    const result = { ...existing };
    for (const [key, newItems] of Object.entries(incoming)) {
        if (!Array.isArray(newItems) || !newItems.some(v => v?.trim())) continue;
        const existingItems = result[key] || [];
        const seen = new Set(existingItems.map(v => v?.trim().toLowerCase()).filter(Boolean));
        const merged = [...existingItems];
        for (const item of newItems) {
            if (item?.trim() && !seen.has(item.trim().toLowerCase())) {
                merged.push(item);
                seen.add(item.trim().toLowerCase());
            }
        }
        result[key] = merged;
    }
    return result;
};

/**
 * Build a version of `processedData` with null/empty scalar values removed,
 * so that spreading it over existing profile data never overwrites a real value
 * with nothing. Array/object fields are handled separately by the callers.
 */
const filterNonEmptyScalars = (processedData, arrayKeys) => {
    return Object.fromEntries(
        Object.entries(processedData).filter(([key, value]) => {
            if (arrayKeys.includes(key)) return false; // handled separately
            return !isEmptyValue(value);
        })
    );
};
// ────────────────────────────────────────────────────────────────────────────

const Profile = () => {
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState({
        "resume_url": "",
        "resume_source_type": "google_doc",
        "latex_main_tex_path": "",
        "latex_file_manifest": [],
        "first name": "",
        "last name": "",
        "email": "",
        "date of birth": "",
        "gender": "",
        "nationality": "",
        "preferred language": "",
        "phone": "",
        "address": "",
        "city": "",
        "state": "",
        "zip": "",
        "country": "",
        "country_code": "",
        "state_code": "",
        "linkedin": "",
        "github": "",
        "other links": [""],
        "education": [
            {
                "degree": "",
                "institution": "",
                "graduation_year": "",
                "gpa": "",
                "relevant_courses": [""]
            }
        ],
        "work experience": [
            {
                "title": "",
                "company": "",
                "start_date": "",
                "end_date": "",
                "description": "",
                "achievements": [""]
            }
        ],
        "projects": [
            {
                "name": "",
                "dates": "",
                "description": "",
                "technologies": [""],
                "github_url": "",
                "live_url": "",
                "features": [""]
            }
        ],
        "skills": {
            "technical": [""],
            "programming_languages": [""],
            "frameworks": [""],
            "tools": [""],
            "soft_skills": [""],
            "languages": [""]
        },
        "summary": "",
        "disabilities": [],
        "veteran status": "",
        "visa status": "",
        "visa sponsorship": "",
        "preferred location": [""],
        "willing to relocate": ""
    });

    // Auto-save system
    const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
    const [profileLoaded, setProfileLoaded] = useState(false);
    const lastSavedRef = useRef(null);

    // Check Google OAuth status
    useEffect(() => {
        const checkGoogleStatus = async () => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setCheckingGoogleStatus(false);
                return;
            }

            setCheckingGoogleStatus(true);
            try {
                const response = await apiClient.get('/api/oauth/status');

                if (response.data.success) {
                    setGoogleConnected(response.data.is_connected);
                    setGoogleEmail(response.data.google_email || '');
                    setGoogleTokenExpired(response.data.token_expired === true);
                }
            } catch (error) {
                console.error('Error checking Google status:', error);
            } finally {
                setCheckingGoogleStatus(false);
            }
        };

        checkGoogleStatus();
    }, []);

    useEffect(() => {
        const loadProfile = async () => {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                // If no token, redirect to login
                navigate('/login');
                return;
            }

            try {
                // Load profile from PostgreSQL database
                const response = await apiClient.get('/api/profile');

                if (response.data.success) {
                    const parsedData = response.data.profile || response.data.resumeData || {};

                    // Restore resume state from DB
                    const srcType = parsedData.resume_source_type || '';
                    setResumeSourceType(srcType);
                    if (srcType === 'pdf' || srcType === 'docx') {
                        // A file was previously uploaded - show the file info card
                        setResumeFileName(parsedData.resume_filename || `resume.${srcType}`);
                        setResumeUrl('');
                        setResumeProcessed(true);
                        setResumeUploadAttempted(true);
                    } else if (parsedData.resume_url) {
                        // Google Doc URL (explicit or legacy with no source_type)
                        setResumeUrl(parsedData.resume_url);
                        setResumeSourceType('google_doc');
                        setResumeProcessed(true);
                        setResumeUploadAttempted(true);
                    }

                    // Load cover letter template if exists
                    if (parsedData.cover_letter_template) {
                        setCoverLetterTemplate(parsedData.cover_letter_template);
                    }

                    // Ensure skills object has proper structure
                    if (!parsedData.skills) {
                        parsedData.skills = {
                            "technical": [""],
                            "programming_languages": [""],
                            "frameworks": [""],
                            "tools": [""],
                            "soft_skills": [""],
                            "languages": [""]
                        };
                    } else {
                        // Ensure each skills category exists and is an array
                        const skillCategories = ["technical", "programming_languages", "frameworks", "tools", "soft_skills", "languages"];
                        skillCategories.forEach(category => {
                            if (!parsedData.skills[category] || !Array.isArray(parsedData.skills[category])) {
                                parsedData.skills[category] = [""];
                            }
                        });
                    }

                    // Ensure array fields are properly initialized
                    const arrayFields = ["other links", "education", "work experience", "projects", "disabilities", "preferred location", "latex_file_manifest"];
                    arrayFields.forEach(field => {
                        if (!parsedData[field] || !Array.isArray(parsedData[field])) {
                            if (field === "other links") {
                                parsedData[field] = [""];
                            } else if (field === "education") {
                                parsedData[field] = [{
                                    "degree": "",
                                    "institution": "",
                                    "graduation_year": "",
                                    "gpa": "",
                                    "relevant_courses": [""]
                                }];
                            } else if (field === "work experience") {
                                parsedData[field] = [{
                                    "title": "",
                                    "company": "",
                                    "start_date": "",
                                    "end_date": "",
                                    "description": "",
                                    "achievements": [""]
                                }];
                            } else if (field === "projects") {
                                parsedData[field] = [{
                                    "name": "",
                                    "description": "",
                                    "technologies": [""],
                                    "github_url": "",
                                    "live_url": "",
                                    "features": [""]
                                }];
                            } else {
                                parsedData[field] = [""];
                            }
                        }
                    });

                    // Ensure string fields are initialized
                    const stringFields = ["resume_url", "resume_source_type", "latex_main_tex_path", "first name", "last name", "email", "date of birth", "gender", 
                                       "nationality", "preferred language", "phone", "address", "city", "state", 
                                       "zip", "country", "country_code", "state_code", "linkedin", "github", 
                                       "summary", "veteran status", "visa status", "visa sponsorship", "willing to relocate"];
                    stringFields.forEach(field => {
                        if (parsedData[field] === undefined || parsedData[field] === null) {
                            parsedData[field] = "";
                        }
                    });

                    // Surface any pending email change
                    if (parsedData.pending_email) {
                        setPendingEmail(parsedData.pending_email);
                    }

                    setProfileData(parsedData);
                    lastSavedRef.current = JSON.stringify(parsedData);
                    setProfileLoaded(true);
                } else {
                    throw new Error('Failed to load profile from database');
                }
            } catch (error) {
                console.error('Error loading profile from database:', error);

                if (error.response?.status === 401) {
                    // Token expired, redirect to login
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    navigate('/login');
                } else {
                    // For other errors, just show the default empty profile structure
                    console.log('Using default profile structure');
                }
            }
        };

        const loadAiKeySettings = async () => {
            try {
                const resp = await apiClient.get('/api/settings/ai-keys');
                if (resp.data.success) {
                    const primary = resp.data.api_primary_mode;
                    setAiPrimaryMode(primary || 'launchway');
                    setAiSecondaryMode(resp.data.api_secondary_mode || '');
                    setAiHasCustomKey(resp.data.has_custom_key || false);
                    setAiMaskedKey(resp.data.masked_custom_key || '');
                    // Show setup modal only when user has never configured their AI Engine
                    if (!primary) setShowAiSetupModal(true);
                }
            } catch (_) {}
        };

        loadProfile();
        loadAiKeySettings();
    }, [navigate]);

    const [activeSection, setActiveSection] = useState('resume');

    //location management states
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [availableCities, setAvailableCities] = useState([]);
    const [availableStates, setAvailableStates] = useState([]);
    const [isLoadingZip] = useState(false);

    const [resumeUrl, setResumeUrl] = useState('');
    const [resumeFileName, setResumeFileName] = useState('');      // name of uploaded PDF/DOCX
    const [resumeSourceType, setResumeSourceType] = useState('');  // 'google_doc' | 'pdf' | 'docx'
    const [isProcessingResume, setIsProcessingResume] = useState(false);
    const [isOpeningPicker, setIsOpeningPicker] = useState(false);
    const [isRemovingResume, setIsRemovingResume] = useState(false);

    // Debounced auto-save: fires 3s after the user stops editing
    useEffect(() => {
        if (!profileLoaded || isProcessingResume) return;

        const currentJson = JSON.stringify(profileData);
        if (currentJson === lastSavedRef.current) {
            return;
        }

        const timer = setTimeout(async () => {
            setAutoSaveStatus('saving');
            try {
                const response = await apiClient.post('/api/profile', profileData);
                if (response.data.success) {
                    lastSavedRef.current = JSON.stringify(profileData);
                    setAutoSaveStatus('saved');
                    setTimeout(() => setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 2500);
                } else {
                    setAutoSaveStatus('error');
                }
            } catch (err) {
                console.error('Auto-save failed:', err);
                setAutoSaveStatus('error');
            }
        }, 3000);

        return () => clearTimeout(timer);
    }, [profileData, profileLoaded, isProcessingResume]);

    // Warn on browser close / reload if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!profileLoaded || !lastSavedRef.current) return;
            if (JSON.stringify(profileData) !== lastSavedRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [profileData, profileLoaded]);

    const [resumeProcessed, setResumeProcessed] = useState(false);
    const [showReUploadForm, setShowReUploadForm] = useState(false);
    const [resumeUploadAttempted, setResumeUploadAttempted] = useState(false);
    const [googleConnected, setGoogleConnected] = useState(false);
    const [googleEmail, setGoogleEmail] = useState('');
    const [googleTokenExpired, setGoogleTokenExpired] = useState(false);
    const [checkingGoogleStatus, setCheckingGoogleStatus] = useState(true);

    // Cover Letter Template states
    const [coverLetterTemplate, setCoverLetterTemplate] = useState('');
    const [coverLetterSaved, setCoverLetterSaved] = useState(false);

    // Account Settings states
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [deleteData, setDeleteData] = useState({
        password: '',
        confirmation: ''
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Email change states
    const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [emailChangeData, setEmailChangeData] = useState({ new_email: '', confirm_email: '' });
    const [emailChangeLoading, setEmailChangeLoading] = useState(false);
    const [emailChangeSuccess, setEmailChangeSuccess] = useState('');
    const [emailChangeError, setEmailChangeError] = useState('');

    // AI Engine states
    const [aiPrimaryMode, setAiPrimaryMode] = useState('launchway');
    const [aiSecondaryMode, setAiSecondaryMode] = useState('');     // '' means "none"
    const [aiCustomKey, setAiCustomKey] = useState('');
    const [aiMaskedKey, setAiMaskedKey] = useState('');
    const [aiHasCustomKey, setAiHasCustomKey] = useState(false);
    const [aiSaving, setAiSaving] = useState(false);
    const [aiSaved, setAiSaved] = useState(false);
    const [aiError, setAiError] = useState('');
    const [showAiSetupModal, setShowAiSetupModal] = useState(false);

    // Resume file upload states
    const [resumeFile, setResumeFile] = useState(null);
    const [uploadMethod, setUploadMethod] = useState('url'); // 'url' | 'file'

    // Toast notification system
    const [toasts, setToasts] = useState([]);
    const addToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }, []);
    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Listen for OAuth callback messages (after addToast exists for exhaustive-deps)
    useEffect(() => {
        const expectedOrigin = new URL(API_URL).origin;
        const handleMessage = async (event) => {
            if (event.origin !== expectedOrigin || !event.data || typeof event.data !== 'object') {
                return;
            }

            if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
                // Update state immediately
                setGoogleConnected(true);
                setGoogleEmail(event.data.email);

                // Re-check Google status from backend to confirm
                try {
                    const response = await apiClient.get('/api/oauth/status');
                    if (response.data.success) {
                        setGoogleConnected(response.data.is_connected);
                        setGoogleEmail(response.data.google_email || event.data.email);
                        setGoogleTokenExpired(false);
                    }
                } catch (error) {
                    console.error('Error re-checking Google status:', error);
                }

                addToast(`Google account connected - ${event.data.email}`, 'success');
            } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
                addToast(`Failed to connect Google account: ${event.data.error}`, 'error');
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [addToast]);

    const validateGoogleDocUrl = (url) => {
        const googleDocUrlPattern = /^https:\/\/docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+(\/.*)?$/;
        return googleDocUrlPattern.test(url);
    };

    const handleConnectGoogle = async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            addToast('Please log in first', 'error');
            return;
        }

        try {
            const response = await apiClient.get('/api/oauth/authorize');

            if (response.data.success) {
                // Open OAuth popup
                const width = 600;
                const height = 700;
                const left = window.screenX + (window.outerWidth - width) / 2;
                const top = window.screenY + (window.outerHeight - height) / 2;

                window.open(
                    response.data.authorization_url,
                    'Google OAuth',
                    `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
                );
            }
        } catch (error) {
            console.error('Error getting authorization URL:', error);
            addToast('Failed to initiate Google connection', 'error');
        }
    };

    const handleDisconnectGoogle = async () => {
        if (!window.confirm('Are you sure you want to disconnect your Google account? You will not be able to tailor resumes until you reconnect.')) {
            return;
        }

        const token = localStorage.getItem('auth_token');
        if (!token) return;

        try {
            const response = await apiClient.post('/api/oauth/disconnect');

            if (response.data.success) {
                setGoogleConnected(false);
                setGoogleEmail('');
                addToast('Google account disconnected', 'success');
            }
        } catch (error) {
            console.error('Error disconnecting Google account:', error);
            addToast('Failed to disconnect Google account', 'error');
        }
    };

    const handleInputChange = (section, field, value, index = null, nestedIndex = null) => {
        setProfileData(prevData => {
            const updatedData = { ...prevData };

            if (index !== null) {
                // Special handling for skills object
                if (section === "skills" && field && typeof updatedData.skills === 'object') {
                    const skillArray = [...(updatedData.skills[field] || [])];
                    skillArray[index] = value;
                    updatedData.skills = {
                        ...updatedData.skills,
                        [field]: skillArray
                    };
                } else if (Array.isArray(updatedData[section])) {
                    updatedData[section] = [...updatedData[section]];
                    const currentItem = updatedData[section][index];
                    // If editing an array of objects and a field is specified, update the field
                    if (field !== null && currentItem && typeof currentItem === 'object' && !Array.isArray(currentItem)) {
                        if (nestedIndex !== null) {
                            const nestedArray = Array.isArray(currentItem[field]) ? [...currentItem[field]] : [];
                            nestedArray[nestedIndex] = value;
                            updatedData[section][index] = { ...currentItem, [field]: nestedArray };
                        } else {
                            updatedData[section][index] = { ...currentItem, [field]: value };
                        }
                    } else {
                        // Otherwise, treat it as a primitive array (e.g., strings) and set the value directly
                        updatedData[section][index] = value;
                    }
                } else {
                    updatedData[section] = [...updatedData[section]];
                    updatedData[section][index] = value;
                }
            } else {
                updatedData[section] = value;
            }

            return updatedData;
        });
    };

    const addArrayItem = (section, template = {}) => {
        setProfileData(prev => ({
            ...prev,
            [section]: Array.isArray(prev[section]) ? [...prev[section], template] : [template]
        }));
    };

    const removeArrayItem = (section, index) => {
        setProfileData(prev => ({
            ...prev,
            [section]: prev[section].filter((_, i) => i !== index)
        }));
    };

    const addArrayField = (section, index, field, template = "") => {
        setProfileData(prev => {
            const newData = { ...prev };
            newData[section] = [...newData[section]];
            newData[section][index] = { ...newData[section][index] };
            const currentField = newData[section][index][field];
            const safeFieldArray = Array.isArray(currentField) ? currentField : [];
            newData[section][index][field] = [...safeFieldArray, template];
            return newData;
        });
    };

    const addSkillToCategory = (category, skill = "") => {
        setProfileData(prev => ({
            ...prev,
            skills: {
                ...prev.skills,
                [category]: [...(prev.skills[category] || []), skill]
            }
        }));
    };

    const removeSkillFromCategory = (category, index) => {
        setProfileData(prev => ({
            ...prev,
            skills: {
                ...prev.skills,
                [category]: (prev.skills[category] || []).filter((_, i) => i !== index)
            }
        }));
    };

    const removeArrayField = (section, index, field, fieldIndex) => {
        setProfileData(prev => {
            const newData = { ...prev };
            newData[section] = [...newData[section]];
            newData[section][index] = { ...newData[section][index] };
            const currentField = newData[section][index][field];
            const safeFieldArray = Array.isArray(currentField) ? currentField : [];
            newData[section][index][field] = safeFieldArray.filter((_, i) => i !== fieldIndex);
            return newData;
        });
    };

    const normalizeGoogleDocUrl = (url) => {
        // Extract the document ID and create a clean edit URL
        const match = url.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
        if (match) {
            return `https://docs.google.com/document/d/${match[1]}/edit`;
        }
        return url;
    };

    const processResumewithLLM = async (urlOverride = null) => {
        const targetUrl = urlOverride || resumeUrl;
        setResumeUploadAttempted(true);
        if (!validateGoogleDocUrl(targetUrl) || !targetUrl) {
            addToast('Please enter a valid Google Doc URL', 'error');
            return;
        }

        const normalizedUrl = normalizeGoogleDocUrl(targetUrl);
        setResumeUrl(normalizedUrl);

        setIsProcessingResume(true);

        try {
            const response = await apiClient.post('/api/process-resume', {
                resume_url: normalizedUrl
            });

            if (response.data.success) {
                // Merge the processed data with existing profile data to preserve structure
                const processedData = response.data.profile_data;

                if (!processedData || Object.keys(processedData).length === 0) {
                    console.error('No profile data returned from backend!');
                    addToast('Resume was processed but no data could be extracted. Please check the format.', 'error');
                    setIsProcessingResume(false);
                    return;
                }

                // Array/object keys that are handled explicitly below (not via scalar spread).
                const ARRAY_KEYS = ["other links", "education", "work experience", "projects", "skills", "disabilities", "preferred location", "latex_file_manifest"];

                // Spread only the non-empty scalar fields from processedData so that a field
                // the LLM didn't find never silently wipes a value the user entered manually.
                const mergedData = {
                    // Preserve all user-entered fields as baseline
                    ...profileData,
                    // Overwrite with LLM scalar fields only when they have a real value
                    ...filterNonEmptyScalars(processedData, ARRAY_KEYS),
                    // Resume-metadata overrides (always explicit)
                    "resume_url": normalizedUrl,
                    "resume_source_type": "google_doc",
                    "resume_text": "",
                    "resume_filename": "",
                    "latex_main_tex_path": "",
                    "latex_file_manifest": [],
                    // Array fields: MERGE with existing pool so manually-added entries survive.
                    "other links": mergeItemPool(profileData["other links"], processedData["other links"], "url")
                        || (profileData["other links"]?.length ? profileData["other links"] : [""]),
                    "education": mergeItemPool(profileData["education"], processedData["education"], "institution")
                        || (profileData["education"]?.length ? profileData["education"] : [{
                            "degree": "", "institution": "", "graduation_year": "", "gpa": "", "relevant_courses": [""]
                        }]),
                    "work experience": mergeItemPool(profileData["work experience"], processedData["work experience"], "company")
                        || (profileData["work experience"]?.length ? profileData["work experience"] : [{
                            "title": "", "company": "", "start_date": "", "end_date": "", "description": "", "achievements": [""]
                        }]),
                    // Projects: full pool merge — extra projects the user added are never deleted.
                    "projects": mergeItemPool(profileData["projects"], processedData["projects"], "name")
                        || (profileData["projects"]?.length ? profileData["projects"] : [{
                            "name": "", "dates": "", "description": "", "technologies": [""], "github_url": "", "live_url": "", "features": [""]
                        }]),
                    // Skills: union each category so manually-added skills are kept.
                    "skills": mergeSkills(profileData["skills"], processedData["skills"])
                        || profileData["skills"] || {
                            "technical": [""], "programming_languages": [""], "frameworks": [""], "tools": [""], "soft_skills": [""], "languages": [""]
                        },
                    "disabilities": profileData["disabilities"] || [],
                    "preferred location": profileData["preferred location"]?.length ? profileData["preferred location"] : [""],
                };

                setProfileData(mergedData);

                try {
                    await apiClient.post('/api/profile', mergedData);
                    lastSavedRef.current = JSON.stringify(mergedData);
                } catch (persistErr) {
                    console.error('Failed to persist profile data to backend:', persistErr);
                }
                setResumeFileName('');
                setResumeSourceType('google_doc');
                setResumeProcessed(true);
                setShowReUploadForm(false);
                setResumeUploadAttempted(true);
                addToast('Resume processed - your profile has been populated', 'success');
            }
            else {
                addToast('Error processing resume. Please try again.', 'error');
            }
        }
        catch (error) {
            console.error('Error processing resume:', error);
            addToast(error.response?.data?.error || 'Error processing resume. Please try again.', 'error');
        }
        finally {
            setIsProcessingResume(false);
        }

    }

    const openGooglePicker = async () => {
        setIsOpeningPicker(true);
        try {
            const [tokenResp, configResp] = await Promise.all([
                apiClient.get('/api/oauth/access-token'),
                apiClient.get('/api/oauth/picker-config'),
            ]);
            const accessToken = tokenResp.data.access_token;
            const { api_key } = configResp.data;

            // Load gapi + picker if not already available
            await new Promise((resolve, reject) => {
                if (window.google?.picker) { resolve(); return; }
                const existing = document.getElementById('gapi-script');
                if (existing) {
                    existing.addEventListener('load', () =>
                        window.gapi.load('picker', { callback: resolve, onerror: reject })
                    );
                    return;
                }
                const script = document.createElement('script');
                script.id = 'gapi-script';
                script.src = 'https://apis.google.com/js/api.js';
                script.onload = () => window.gapi.load('picker', { callback: resolve, onerror: reject });
                script.onerror = () => reject(new Error('Failed to load Google API script'));
                document.head.appendChild(script);
            });

            setIsOpeningPicker(false);

            const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
                .setMimeTypes('application/vnd.google-apps.document')
                .setMode(window.google.picker.DocsViewMode.LIST);

            new window.google.picker.PickerBuilder()
                .addView(view)
                .setOAuthToken(accessToken)
                .setDeveloperKey(api_key)
                .setTitle('Select your resume Google Doc')
                .setCallback((data) => {
                    if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
                        const doc = data[window.google.picker.Response.DOCUMENTS][0];
                        const fileId = doc[window.google.picker.Document.ID];
                        const fileUrl = `https://docs.google.com/document/d/${fileId}/edit`;
                        processResumewithLLM(fileUrl);
                    }
                })
                .build()
                .setVisible(true);

        } catch (err) {
            console.error('Picker error:', err);
            addToast(
                err.response?.data?.error || 'Failed to open Google Drive picker. Please try again.',
                'error'
            );
            setIsOpeningPicker(false);
        }
    };

    const handleResumeFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!validTypes.includes(file.type)) {
                addToast('Please upload a PDF or DOCX file', 'error');
                event.target.value = '';
                return;
            }

            // Validate file size (10MB)
            if (file.size > 10 * 1024 * 1024) {
                addToast('File is too large. Maximum size is 10 MB.', 'error');
                event.target.value = '';
                return;
            }

            setResumeFile(file);
            setResumeProcessed(false);
        }
    };

    const processResumeFile = async () => {
        setResumeUploadAttempted(true);
        if (!resumeFile) {
            addToast('Please select a file first', 'error');
            return;
        }

        setIsProcessingResume(true);

        try {
            const formData = new FormData();
            formData.append('resume', resumeFile);

            const response = await apiClient.post('/api/upload-resume', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                const processedData = response.data.profile_data;
                const sourceType = response.data.source_type || 'pdf';

                if (!processedData || Object.keys(processedData).length === 0) {
                    addToast('Resume was processed but no data could be extracted. Check your file format.', 'error');
                    setIsProcessingResume(false);
                    return;
                }

                const PDF_ARRAY_KEYS = ["other links", "education", "work experience", "projects", "skills", "disabilities", "preferred location", "latex_file_manifest"];

                const mergedData = {
                    // Preserve all user-entered fields as baseline
                    ...profileData,
                    // Overwrite with LLM scalar fields only when they have a real value
                    ...filterNonEmptyScalars(processedData, PDF_ARRAY_KEYS),
                    "resume_url": "",
                    "resume_source_type": sourceType,
                    "resume_filename": resumeFile.name,
                    "latex_main_tex_path": "",
                    "latex_file_manifest": [],
                    // Array fields: MERGE with existing pool so manually-added entries survive.
                    "other links": mergeItemPool(profileData["other links"], processedData["other links"], "url")
                        || (profileData["other links"]?.length ? profileData["other links"] : [""]),
                    "education": mergeItemPool(profileData["education"], processedData["education"], "institution")
                        || (profileData["education"]?.length ? profileData["education"] : [{
                            "degree": "", "institution": "", "graduation_year": "", "gpa": "", "relevant_courses": [""]
                        }]),
                    "work experience": mergeItemPool(profileData["work experience"], processedData["work experience"], "company")
                        || (profileData["work experience"]?.length ? profileData["work experience"] : [{
                            "title": "", "company": "", "start_date": "", "end_date": "", "description": "", "achievements": [""]
                        }]),
                    // Projects: full pool merge — extra projects the user added are never deleted.
                    "projects": mergeItemPool(profileData["projects"], processedData["projects"], "name")
                        || (profileData["projects"]?.length ? profileData["projects"] : [{
                            "name": "", "dates": "", "description": "", "technologies": [""], "github_url": "", "live_url": "", "features": [""]
                        }]),
                    // Skills: union each category so manually-added skills are kept.
                    "skills": mergeSkills(profileData["skills"], processedData["skills"])
                        || profileData["skills"] || {
                            "technical": [""], "programming_languages": [""], "frameworks": [""], "tools": [""], "soft_skills": [""], "languages": [""]
                        },
                    "disabilities": profileData["disabilities"] || [],
                    "preferred location": profileData["preferred location"]?.length ? profileData["preferred location"] : [""],
                };

                setProfileData(mergedData);
                setResumeUrl('');
                setResumeFileName(response.data.resume_filename || resumeFile.name);
                setResumeSourceType(sourceType);

                try {
                    await apiClient.post('/api/profile', mergedData);
                    lastSavedRef.current = JSON.stringify(mergedData);
                } catch (persistErr) {
                    console.error('Failed to persist profile data:', persistErr);
                }

                setResumeProcessed(true);
                setShowReUploadForm(false);
                setResumeUploadAttempted(true);
                addToast('Resume uploaded - your profile has been populated', 'success');
            } else {
                addToast('Error processing resume file. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error uploading resume:', error);
            addToast(error.response?.data?.error || 'Error uploading resume. Please try again.', 'error');
        } finally {
            setIsProcessingResume(false);
        }
    };

    const handleRemoveResume = async () => {
        if (isRemovingResume || isProcessingResume) return;

        const confirmed = window.confirm(
            'Remove your resume from profile? This will disable tailoring until you upload a new one.'
        );
        if (!confirmed) return;

        setIsRemovingResume(true);

        try {
            const clearedProfile = {
                ...profileData,
                resume_url: '',
                resume_source_type: '',
                resume_text: '',
                resume_filename: '',
                resume_file_base64: '',
                latex_main_tex_path: '',
                latex_file_manifest: [],
                resume_keywords: null,
            };

            const response = await apiClient.post('/api/profile', clearedProfile);
            if (!response.data.success) {
                throw new Error('Failed to remove resume from profile');
            }

            setProfileData(clearedProfile);
            lastSavedRef.current = JSON.stringify(clearedProfile);
            setResumeUrl('');
            setResumeFile(null);
            setResumeFileName('');
            setResumeSourceType('');
            setResumeProcessed(false);
            setShowReUploadForm(false);
            setResumeUploadAttempted(false);
            addToast('Resume removed from your profile', 'success');
        } catch (error) {
            console.error('Error removing resume:', error);
            addToast(error.response?.data?.error || 'Failed to remove resume. Please try again.', 'error');
        } finally {
            setIsRemovingResume(false);
        }
    };


    // Location management functions
    const getCountries = () => {
        return Country.getAllCountries().map(country => ({
            value: country.isoCode,
            label: country.name,
            code: country.isoCode
        }));
    };

    const getStates = (countryCode) => {
        if (!countryCode) return [];
        return State.getStatesOfCountry(countryCode).map(state => ({
            value: state.isoCode,
            label: state.name,
            code: state.isoCode
        }));
    };

    const getCities = (countryCode, stateCode) => {
        if (!countryCode || !stateCode) return [];
        return City.getCitiesOfState(countryCode, stateCode).map(city => ({
            value: city.name,
            label: city.name,
            name: city.name
        }));
    };

    // Location management functions
    const handleCountryChange = (selectedCountry) => {
        setSelectedCountry(selectedCountry);
        setSelectedState(null);
        setSelectedCity(null);
        setAvailableStates([]);
        setAvailableCities([]);

        if (selectedCountry) {
            const states = getStates(selectedCountry.value);
            setAvailableStates(states);
            setProfileData(prevData => ({
                ...prevData,
                country: selectedCountry.label,
                country_code: selectedCountry.value,
            }));
        }
        else {
            setProfileData(prevData => ({
                ...prevData,
                country: '',
                country_code: '',
            }));
        }
    };

    const handleStateChange = (selectedState) => {
        setSelectedState(selectedState);
        setSelectedCity(null);
        setAvailableCities([]);

        if (selectedState) {
            const cities = getCities(selectedCountry.value, selectedState.value);
            setAvailableCities(cities);
            setProfileData(prevData => ({
                ...prevData,
                state: selectedState.label,
                state_code: selectedState.value
            }));
        }
        else {
            setProfileData(prevData => ({
                ...prevData,
                state: '',
                state_code: ''
            }));
        }
    };

    const handleCityChange = (selectedCity) => {
        setSelectedCity(selectedCity);
        if (selectedCity) {
            setProfileData(prevData => ({
                ...prevData,
                city: selectedCity.label,
            }));

        } else {
            setProfileData(prevData => ({
                ...prevData,
                city: ''
            }));
        }
    };

    const saveProfile = async () => {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            navigate('/login');
            return;
        }

        const merged = {
            ...profileData,
            //country: selectedCountry.label,
            //country_code: selectedCountry.value,
            //state: selectedState.label,
            //state_code: selectedState.value,
            //city: selectedCity.label,
        };

        try {
            const response = await apiClient.post('/api/profile', merged);

            if (response.data.success) {
                setProfileData(merged);
                lastSavedRef.current = JSON.stringify(merged);
                setAutoSaveStatus('saved');
                setTimeout(() => setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 2500);
                addToast('Profile saved successfully', 'success');
            } else {
                throw new Error(response.data.error || 'Failed to save profile');
            }
        } catch (error) {
            console.error('Error saving profile:', error);

            if (error.response?.status === 401) {
                // Token expired, redirect to login
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                navigate('/login');
                return;
            }

            // Show error to user
            addToast('Failed to save profile: ' + (error.response?.data?.error || error.message), 'error');
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.new_password !== passwordData.confirm_password) {
            addToast('New passwords do not match', 'error');
            return;
        }

        if (passwordData.new_password.length < 8) {
            addToast('New password must be at least 8 characters', 'error');
            return;
        }

        setIsChangingPassword(true);

        try {
            const response = await apiClient.post('/api/account/change-password', {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password
            });

            if (response.data.success) {
                addToast('Password changed successfully', 'success');
                setShowChangePassword(false);
                setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                });
            }
        } catch (error) {
            console.error('Error changing password:', error);
            addToast(error.response?.data?.error || 'Failed to change password', 'error');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleEmailChangeRequest = async () => {
        const { new_email, confirm_email } = emailChangeData;
        setEmailChangeError('');
        setEmailChangeSuccess('');

        if (!new_email || !new_email.includes('@')) {
            setEmailChangeError('Please enter a valid email address.');
            return;
        }
        if (new_email !== confirm_email) {
            setEmailChangeError('Email addresses do not match.');
            return;
        }
        if (new_email.toLowerCase() === (profileData.email || '').toLowerCase()) {
            setEmailChangeError('The new email is the same as your current email.');
            return;
        }

        setEmailChangeLoading(true);
        try {
            const response = await apiClient.post('/api/account/request-email-change', { new_email });
            if (response.data.success) {
                setEmailChangeSuccess(response.data.message);
                setPendingEmail(new_email);
                setEmailChangeData({ new_email: '', confirm_email: '' });
            } else {
                setEmailChangeError(response.data.error || 'Failed to initiate email change.');
            }
        } catch (error) {
            setEmailChangeError(error.response?.data?.error || 'Failed to send verification email. Please try again.');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleExportData = async () => {
        try {
            const response = await apiClient.get('/api/account/export-data');

            if (response.data.success) {
                // Create downloadable JSON file
                const dataStr = JSON.stringify(response.data.data, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = window.URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `job-agent-data-export-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                addToast('Your data has been exported successfully', 'success');
            }
        } catch (error) {
            console.error('Error exporting data:', error);
            addToast(error.response?.data?.error || 'Failed to export data', 'error');
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteData.confirmation !== 'DELETE') {
            addToast('Please type DELETE to confirm', 'error');
            return;
        }

        if (!window.confirm(
            'Are you absolutely sure? This action cannot be undone.\n\n' +
            'All your data including:\n' +
            '- Profile information\n' +
            '- Resume data\n' +
            '- Account history\n' +
            '\nwill be permanently deleted.'
        )) {
            return;
        }

        setIsDeletingAccount(true);

        try {
            const response = await apiClient.delete('/api/account/delete', {
                data: {
                    password: deleteData.password,
                    confirmation: deleteData.confirmation
                }
            });

            if (response.data.success) {
                addToast('Your account has been permanently deleted. Logging out...', 'success', 3000);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                navigate('/login');
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            addToast(error.response?.data?.error || 'Failed to delete account', 'error');
            setIsDeletingAccount(false);
        }
    };

    // Initialize location data from profile
    useEffect(() => {
        if (profileData.country) {
            const countries = getCountries();
            const country = countries.find(c => c.label === profileData.country);
            if (country) {
                setSelectedCountry(country);
                const states = getStates(country.value);
                setAvailableStates(states);

                if (profileData.state) {
                    const state = states.find(s => s.label === profileData.state);
                    if (state) {
                        setSelectedState(state);
                        const cities = getCities(country.value, state.value);
                        setAvailableCities(cities);

                        if (profileData.city) {
                            const city = cities.find(c => c.label === profileData.city);
                            if (city) {
                                setSelectedCity(city);
                            }
                        }
                    }
                }
            }
        }
    }, [profileData.country, profileData.state, profileData.city]);

    const resumeuploadsection = () => (
        <div className='form-section'>
            <h1>Resume Upload</h1>
            <p className="section-subtitle">Upload your resume to auto-populate your profile and enable AI-powered tailoring</p>

            {/* ─────────────────────────────────────────────────────────────
                BRANCH 1 - Loading skeleton
               ───────────────────────────────────────────────────────────── */}
            {checkingGoogleStatus ? (
                <div className="resume-upload-skeleton">
                    <div className="resume-upload-skeleton__card"><div className="skeleton-shimmer" /></div>
                    <div className="resume-upload-skeleton__card"><div className="skeleton-shimmer" /></div>
                </div>

            /* ─────────────────────────────────────────────────────────────
                BRANCH 2 - Resume already present (show preview + cover letter)
               ───────────────────────────────────────────────────────────── */
            ) : resumeProcessed && !showReUploadForm ? (
                <>
                    {/* Google connection bar (compact) */}
                    {googleConnected && (
                        <div className="google-connected-info">
                            <div className="status-badge connected">
                                <span>Connected to {googleEmail}</span>
                            </div>
                            <button onClick={handleDisconnectGoogle} className="disconnect-btn-small" type="button">
                                Disconnect
                            </button>
                        </div>
                    )}

                    <div className="resume-processed-container">
                        {/* ── Status row ── */}
                        <div className="resume-status-row">
                            <div className="resume-status-row__left">
                                <img src="/assets/check.png" alt="" className="resume-status-row__check" />
                                <div className="resume-status-row__info">
                                    <div className="resume-status-row__title">Resume processed - profile populated</div>
                                    {resumeSourceType === 'google_doc' ? (
                                        <div className="resume-status-pill resume-status-pill--enabled">
                                            <span className="resume-status-dot resume-status-dot--green" />
                                            AI Tailoring Enabled
                                        </div>
                                    ) : (
                                        <div className="resume-status-pill resume-status-pill--disabled">
                                            <span className="resume-status-dot resume-status-dot--red" />
                                            AI Tailoring Disabled - PDF/DOCX
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="resume-status-row__actions">
                                <button onClick={() => setActiveSection('personaldetails')} className="success-btn">
                                    Review Profile
                                </button>
                                <button
                                    onClick={() => {
                                        setShowReUploadForm(true);
                                        setResumeFile(null);
                                        setUploadMethod('url');
                                    }}
                                    className="process-another-btn"
                                >
                                    Re-upload
                                </button>
                                <button
                                    onClick={handleRemoveResume}
                                    className="remove-resume-btn"
                                    disabled={isRemovingResume || isProcessingResume}
                                >
                                    {isRemovingResume ? 'Removing...' : 'Remove Resume'}
                                </button>
                            </div>
                        </div>

                        {/* ── Resume preview ── */}
                        {resumeSourceType === 'google_doc' && resumeUrl ? (
                            <>
                                <div className="resume-source-row">
                                    <img className="resume-source-row__icon" src="/assets/icon-resume.png" alt="" />
                                    <span className="resume-source-row__url">{resumeUrl}</span>
                                    <a href={resumeUrl} target="_blank" rel="noopener noreferrer" className="resume-source-row__open">
                                        Open ↗
                                    </a>
                                </div>
                                <div className="resume-iframe-container">
                                    <iframe
                                        src={resumeUrl.replace('/edit', '/preview')}
                                        className="resume-preview-iframe"
                                        title="Resume Preview"
                                    />
                                </div>
                            </>
                        ) : (resumeSourceType === 'pdf' || resumeSourceType === 'docx') ? (
                            <div className="resume-file-card">
                                <div className="resume-file-card__icon">
                                    <img src="/assets/icon-resume.png" alt="" />
                                </div>
                                <div className="resume-file-card__info">
                                    <div className="resume-file-card__name">
                                        {resumeFileName || `resume.${resumeSourceType}`}
                                    </div>
                                    <div className="resume-file-card__meta">
                                        {resumeSourceType.toUpperCase()} · profile extracted & stored
                                    </div>
                                </div>
                                <div className="resume-file-card__badge">Stored</div>
                            </div>
                        ) : null}
                    </div>

                    {/* ── Cover Letter Template ── */}
                    <div className="cover-letter-section">
                        <h3>Cover Letter Template</h3>
                        <p className="section-description">
                            Add your cover letter template. This will be tailored for each job application.
                        </p>
                        <textarea
                            className="cover-letter-textarea"
                            placeholder={"Enter your cover letter template here...\n\nYou can use placeholders like [Company Name], [Job Title], etc.\n\nExample:\nDear Hiring Manager,\n\nI am writing to express my interest in the [Job Title] position at [Company Name]..."}
                            value={coverLetterTemplate}
                            onChange={(e) => setCoverLetterTemplate(e.target.value)}
                            rows={15}
                        />
                        <div className="cover-letter-actions">
                            <button
                                onClick={async () => {
                                    try {
                                        await apiClient.post('/api/profile', {
                                            cover_letter_template: coverLetterTemplate
                                        });
                                        setCoverLetterSaved(true);
                                        setTimeout(() => setCoverLetterSaved(false), 3000);
                                    } catch (error) {
                                        console.error('Error saving cover letter:', error);
                                        addToast('Failed to save cover letter template', 'error');
                                    }
                                }}
                                className="save-cover-letter-btn"
                                disabled={!coverLetterTemplate.trim()}
                            >
                                {coverLetterSaved ? 'Saved!' : 'Save Cover Letter Template'}
                            </button>
                            {coverLetterTemplate && (
                                <span className="character-count">
                                    {coverLetterTemplate.length} characters
                                </span>
                            )}
                        </div>
                    </div>
                </>

            /* ─────────────────────────────────────────────────────────────
                BRANCH 3 - No resume yet / Re-upload mode
               ───────────────────────────────────────────────────────────── */
            ) : (
                <>
                    {/* Re-upload cancel bar */}
                    {showReUploadForm && (
                        <div className="reupload-cancel-bar">
                            <span className="reupload-cancel-bar__label">Replacing current resume</span>
                            <button
                                type="button"
                                className="reupload-cancel-bar__btn"
                                onClick={() => {
                                    setShowReUploadForm(false);
                                    setResumeFile(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                    {/* Google connected - show method selector */}
                    {googleConnected ? (
                        <>
                            <div className="google-connected-info">
                                <div className="status-badge connected">
                                    <span>Connected to {googleEmail}</span>
                                </div>
                                <button onClick={handleDisconnectGoogle} className="disconnect-btn-small" type="button">
                                    Disconnect
                                </button>
                            </div>

                            <div className="url-input-container">
                                <div className="upload-method-selector">
                                    <button
                                        type="button"
                                        className={`method-btn ${uploadMethod === 'url' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('url')}
                                    >
                                        <img className="method-btn__icon" src="/assets/icon-resume.png" alt="" />
                                        Google Drive
                                    </button>
                                    <button
                                        type="button"
                                        className={`method-btn ${uploadMethod === 'file' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('file')}
                                    >
                                        <img className="method-btn__icon" src="/assets/icon-up-loading.png" alt="" />
                                        Upload PDF/DOCX
                                    </button>
                                </div>

                                {uploadMethod === 'url' ? (
                                    <div className="url-input-wrapper">
                                        <div className="info-note" style={{
                                            borderLeft: '4px solid #4CAF50',
                                            backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                            marginBottom: '16px'
                                        }}>
                                            <strong>Resume Tailoring Enabled</strong> — Google Docs is the only format
                                            that supports AI-powered resume tailoring. Private docs are supported.
                                        </div>
                                        <button
                                            onClick={openGooglePicker}
                                            disabled={isOpeningPicker || isProcessingResume}
                                            className={`process-btn ${isOpeningPicker || isProcessingResume ? 'processing' : 'valid'}`}
                                        >
                                            {isOpeningPicker
                                                ? 'Opening Picker...'
                                                : isProcessingResume
                                                ? 'Processing...'
                                                : '📂  Select from Google Drive'}
                                        </button>
                                        {resumeUrl && resumeSourceType === 'google_doc' && (
                                            <div className="info-note" style={{ marginTop: '10px' }}>
                                                Current: <strong>{resumeUrl}</strong>
                                            </div>
                                        )}
                                    </div>
                                ) : uploadMethod === 'file' ? (
                                    <div className="file-upload-wrapper">
                                        <div className="info-note" style={{
                                            borderLeft: '4px solid #FF6B6B',
                                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                            marginBottom: '14px'
                                        }}>
                                            <strong>Resume Tailoring Not Available for PDF/DOCX</strong>
                                            <p style={{ margin: '6px 0 0', fontSize: '0.88rem', lineHeight: '1.5' }}>
                                                Your profile will be populated, but
                                                <strong> AI resume tailoring is disabled</strong> for this format.
                                                Switch to <strong>Google Docs URL</strong> to enable tailoring.
                                            </p>
                                        </div>
                                        <input
                                            type="file"
                                            accept=".pdf,.docx"
                                            onChange={handleResumeFileChange}
                                            className="file-input"
                                            id="resume-file-input"
                                        />
                                        <label htmlFor="resume-file-input" className="file-input-label">
                                            {resumeFile ? (
                                                <>
                                                    <img className="file-icon" src="/assets/icon-attach.png" alt="" />
                                                    <span className="file-name">{resumeFile.name}</span>
                                                    <span className="file-size">({(resumeFile.size / 1024).toFixed(1)} KB)</span>
                                                </>
                                            ) : (
                                                <>
                                                    <img className="upload-icon" src="/assets/icon-up-loading.png" alt="" />
                                                    <span>Choose PDF or DOCX file (max 10MB)</span>
                                                </>
                                            )}
                                        </label>
                                        {resumeFile && (
                                            <button
                                                onClick={processResumeFile}
                                                disabled={isProcessingResume}
                                                className={`process-btn ${isProcessingResume ? 'processing' : 'valid'}`}
                                            >
                                                {isProcessingResume ? 'Processing...' : 'Upload & Extract Profile'}
                                            </button>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </>

                    /* Not connected - two-card option grid */
                    ) : (
                        <div className="resume-upload-options">
                            {googleTokenExpired && (
                                <div className="google-token-expired-banner">
                                    <img className="google-token-expired-banner__icon" src="/assets/icon-disabled.png" alt="" />
                                    <div className="google-token-expired-banner__text">
                                        <strong>Google session expired{googleEmail ? ` for ${googleEmail}` : ''}</strong>
                                        <span>Your previous connection is no longer valid. Please reconnect below.</span>
                                    </div>
                                </div>
                            )}

                            <div className="upload-option-grid">
                                <div className="upload-option-card upload-option-card--primary">
                                    <div className="upload-option-card__header">
                                        <img className="upload-option-card__icon" src="/assets/icon-global.png" alt="" />
                                        <div>
                                            <div className="upload-option-card__title">{googleTokenExpired ? 'Reconnect' : 'Connect'} Google Account</div>
                                            <div className="upload-option-card__badge">Recommended</div>
                                        </div>
                                    </div>
                                    <div className="upload-option-tag upload-option-tag--green">
                                        <span className="upload-option-tag__dot upload-option-tag__dot--green" />
                                        AI Tailoring available
                                    </div>
                                    <button onClick={handleConnectGoogle} className="connect-btn" type="button">
                                        {googleTokenExpired ? 'Reconnect Google' : 'Connect Google Account'}
                                    </button>
                                </div>

                                <div className="upload-option-card">
                                    <div className="upload-option-card__header">
                                        <img className="upload-option-card__icon" src="/assets/icon-up-loading.png" alt="" />
                                        <div>
                                            <div className="upload-option-card__title">Upload PDF or DOCX</div>
                                            <div className="upload-option-card__badge upload-option-card__badge--muted">No Google needed</div>
                                        </div>
                                    </div>
                                    <div className="upload-option-tag upload-option-tag--red">
                                        <span className="upload-option-tag__dot upload-option-tag__dot--red" />
                                        AI Tailoring unavailable - connect Google to enable
                                    </div>
                                    <div className="file-upload-wrapper">
                                        <input
                                            type="file"
                                            accept=".pdf,.docx"
                                            onChange={handleResumeFileChange}
                                            className="file-input"
                                            id="resume-file-input-nogoogle"
                                        />
                                        <label htmlFor="resume-file-input-nogoogle" className="file-input-label">
                                            {resumeFile ? (
                                                <>
                                                    <img className="file-icon" src="/assets/icon-attach.png" alt="" />
                                                    <span className="file-name">{resumeFile.name}</span>
                                                    <span className="file-size">({(resumeFile.size / 1024).toFixed(1)} KB)</span>
                                                </>
                                            ) : (
                                                <>
                                                    <img className="upload-icon" src="/assets/icon-up-loading.png" alt="" />
                                                    <span>Choose PDF or DOCX (max 10MB)</span>
                                                </>
                                            )}
                                        </label>
                                        {resumeFile && (
                                            <button
                                                onClick={processResumeFile}
                                                disabled={isProcessingResume}
                                                className={`process-btn ${isProcessingResume ? 'processing' : 'valid'}`}
                                            >
                                                {isProcessingResume ? 'Processing...' : 'Upload & Extract Profile'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
    const personaldetailssection = () => (
        <div className='form-section'>
            <h1>Personal Details</h1>

            {/* ── Cluster 1: Identity ── */}
            <div className="field-cluster">
                <p className="field-cluster__label">Identity</p>
                <div className="form-grid">
                    <div className="form-group">
                        <label>First Name</label>
                        <input type="text" value={profileData["first name"]}
                            onChange={(e) => handleInputChange("first name", null, e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Last Name</label>
                        <input type="text" value={profileData["last name"]}
                            onChange={(e) => handleInputChange("last name", null, e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Date of Birth</label>
                        <input type="date" value={profileData["date of birth"]}
                            onChange={(e) => handleInputChange("date of birth", null, e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Gender</label>
                        <select value={profileData.gender}
                            onChange={(e) => handleInputChange("gender", null, e.target.value)}>
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Nationality</label>
                        <input type="text" value={profileData.nationality}
                            onChange={(e) => handleInputChange("nationality", null, e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Preferred Language</label>
                        <input type="text" value={profileData["preferred language"]}
                            onChange={(e) => handleInputChange("preferred language", null, e.target.value)} />
                    </div>
                </div>
            </div>

            {/* ── Cluster 2: Contact ── */}
            <div className="field-cluster">
                <p className="field-cluster__label">Contact</p>
                <div className="form-grid">
                    <div className="form-group">
                        <label>Email</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="email" value={profileData.email} readOnly
                                className="email-readonly-input" />
                            <button type="button"
                                onClick={() => {
                                    setEmailChangeData({ new_email: '', confirm_email: '' });
                                    setEmailChangeError('');
                                    setEmailChangeSuccess('');
                                    setShowEmailChangeModal(true);
                                }}
                                style={{
                                    padding: '8px 14px',
                                    background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                                    color: '#fff', border: 'none', borderRadius: '6px',
                                    cursor: 'pointer', fontWeight: '600',
                                    whiteSpace: 'nowrap', fontSize: '13px'
                                }}>
                                Change Email
                            </button>
                        </div>
                        {pendingEmail && (
                            <div style={{
                                marginTop: '6px', padding: '7px 10px',
                                background: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.4)',
                                borderRadius: '6px', fontSize: '12px', color: '#FFD54F'
                            }}>
                                ⏳ Pending change to <strong>{pendingEmail}</strong> - verify via email link.
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input type="tel" value={profileData.phone}
                            onChange={(e) => handleInputChange("phone", null, e.target.value)} />
                    </div>
                </div>
            </div>

            {/* ── Cluster 3: Address ── */}
            <div className="field-cluster">
                <p className="field-cluster__label">Address</p>
                <div className="form-group">
                    <label>Street Address</label>
                    <input type="text" value={profileData.address}
                        onChange={(e) => handleInputChange("address", null, e.target.value)} />
                </div>
                <div className="form-grid">
                    <div className="form-group">
                        <label>Country</label>
                        <Select value={selectedCountry} onChange={handleCountryChange}
                            options={getCountries()} placeholder="Select Country" isSearchable
                            className="react-select-container" classNamePrefix="react-select" />
                    </div>
                    <div className="form-group">
                        <label>State / Province</label>
                        <Select value={selectedState} onChange={handleStateChange}
                            options={availableStates} placeholder="Select State/Province"
                            isSearchable isDisabled={!selectedCountry}
                            className="react-select-container" classNamePrefix="react-select" />
                    </div>
                    <div className="form-group">
                        <label>City</label>
                        <Select value={selectedCity} onChange={handleCityChange}
                            options={availableCities} placeholder="Select City"
                            isSearchable isDisabled={!selectedState}
                            className="react-select-container" classNamePrefix="react-select" />
                    </div>
                    <div className="form-group">
                        <label>ZIP / Postal Code</label>
                        <div className="zip-input-container">
                            <input type="text" value={profileData.zip}
                                onChange={(e) => handleInputChange("zip", null, e.target.value)}
                                placeholder={isLoadingZip ? "Loading..." : "Enter ZIP/Postal Code"}
                                disabled={isLoadingZip} />
                            {isLoadingZip && <div className="loading-spinner">🔄</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Cluster 4: Links ── */}
            <div className="field-cluster">
                <p className="field-cluster__label">Links</p>
                <div className="form-grid">
                    <div className="form-group">
                        <label>LinkedIn</label>
                        <input type="url" value={profileData.linkedin}
                            onChange={(e) => handleInputChange("linkedin", null, e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>GitHub</label>
                        <input type="url" value={profileData.github}
                            onChange={(e) => handleInputChange("github", null, e.target.value)} />
                    </div>
                </div>
                <div className="form-group">
                    <label>Other Links</label>
                    {(profileData["other links"] || [""]).map((link, index) => (
                        <div key={index} className="array-item">
                            <input type="url" value={link}
                                onChange={(e) => handleInputChange("other links", null, e.target.value, index)} />
                            <button type="button" onClick={() => removeArrayItem("other links", index)}
                                className="remove-btn">Remove</button>
                        </div>
                    ))}
                    <button type="button" onClick={() => addArrayItem("other links", "")}
                        className="add-btn">Add Link</button>
                </div>
            </div>

            {/* ── Summary ── */}
            <div className="field-cluster">
                <p className="field-cluster__label">Professional Summary</p>
                <div className="form-group">
                    <label>Bio</label>
                    <textarea
                        value={profileData["summary"]}
                        onChange={(e) => handleInputChange("summary", null, e.target.value)}
                        placeholder="Write 2–4 sentences about your professional background, key skills, and career goals. Used in cover letters and AI tailoring."
                        rows={4}
                        style={{ resize: 'vertical', width: '100%' }}
                    />
                </div>
            </div>
        </div>
    )
    const educationsection = () => (
        <div className='form-section'>
            <h1>Education</h1>
            {(profileData.education || []).map((edu, index) => (
                <div key={index} className="array-section">
                    <div className="section-header">
                        <h4>Education #{index + 1}</h4>
                        <button
                            type="button"
                            onClick={() => removeArrayItem("education", index)}
                            className="remove-section-btn"
                        >
                            Remove
                        </button>
                    </div>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Degree</label>
                            <input
                                type="text"
                                value={edu.degree}
                                onChange={(e) => handleInputChange("education", "degree", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Institution</label>
                            <input
                                type="text"
                                value={edu.institution}
                                onChange={(e) => handleInputChange("education", "institution", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Graduation Year</label>
                            <input
                                type="text"
                                value={edu.graduation_year}
                                onChange={(e) => handleInputChange("education", "graduation_year", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>GPA</label>
                            <input
                                type="text"
                                value={edu.gpa}
                                onChange={(e) => handleInputChange("education", "gpa", e.target.value, index)}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Relevant Courses</label>
                        {(Array.isArray(edu.relevant_courses) ? edu.relevant_courses : []).map((course, courseIndex) => (
                            <div key={courseIndex} className="array-item">
                                <input
                                    type="text"
                                    value={course}
                                    onChange={(e) => handleInputChange("education", "relevant_courses", e.target.value, index, courseIndex)}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeArrayField("education", index, "relevant_courses", courseIndex)}
                                    className="remove-btn"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => addArrayField("education", index, "relevant_courses", "")}
                            className="add-btn"
                        >
                            Add Course
                        </button>
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => addArrayItem("education", {
                    degree: "",
                    institution: "",
                    graduation_year: "",
                    gpa: "",
                    relevant_courses: [""]
                })}
                className="add-section-btn"
            >
                Add Education
            </button>
        </div>
    )
    const workexperiencesection = () => (
        <div className='form-section'>
            <h1>Work Experience</h1>
            {(profileData["work experience"] || []).map((exp, index) => (
                <div key={index} className="array-section">
                    <div className="section-header">
                        <h4>Experience #{index + 1}</h4>
                        <button
                            type="button"
                            onClick={() => removeArrayItem("work experience", index)}
                            className="remove-section-btn"
                        >
                            Remove
                        </button>
                    </div>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                value={exp.title}
                                onChange={(e) => handleInputChange("work experience", "title", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Company</label>
                            <input
                                type="text"
                                value={exp.company}
                                onChange={(e) => handleInputChange("work experience", "company", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Start Date</label>
                            <input
                                type="date"
                                value={exp.start_date}
                                onChange={(e) => handleInputChange("work experience", "start_date", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>End Date</label>
                            <input
                                type="date"
                                value={exp.end_date}
                                onChange={(e) => handleInputChange("work experience", "end_date", e.target.value, index)}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={exp.description}
                            onChange={(e) => handleInputChange("work experience", "description", e.target.value, index)}
                            rows="3"
                        />
                    </div>
                    <div className="form-group">
                        <label>Achievements</label>
                        {(Array.isArray(exp.achievements) ? exp.achievements : []).map((achievement, achievementIndex) => (
                            <div key={achievementIndex} className="array-item">
                                <textarea
                                    value={achievement}
                                    onChange={(e) => handleInputChange("work experience", "achievements", e.target.value, index, achievementIndex)}
                                    rows="2"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeArrayField("work experience", index, "achievements", achievementIndex)}
                                    className="remove-btn"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => addArrayField("work experience", index, "achievements", "")}
                            className="add-btn"
                        >
                            Add Achievement
                        </button>
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => addArrayItem("work experience", {
                    title: "",
                    company: "",
                    start_date: "",
                    end_date: "",
                    description: "",
                    achievements: [""]
                })}
                className="add-section-btn"
            >
                Add Experience
            </button>
        </div>
    )
    const projectssection = () => (
        <div className='form-section'>
            <h1>Projects</h1>
            {(profileData.projects || []).map((project, index) => (
                <div key={index} className="array-section">
                    <div className="section-header">
                        <h4>Project #{index + 1}</h4>
                        <button
                            type="button"
                            onClick={() => removeArrayItem("projects", index)}
                            className="remove-section-btn"
                        >
                            Remove
                        </button>
                    </div>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Project Name</label>
                            <input
                                type="text"
                                value={project.name}
                                onChange={(e) => handleInputChange("projects", "name", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Dates (e.g. Jan 2023 – Present)</label>
                            <input
                                type="text"
                                value={project.dates ?? ""}
                                onChange={(e) => handleInputChange("projects", "dates", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>GitHub URL</label>
                            <input
                                type="url"
                                value={project.github_url}
                                onChange={(e) => handleInputChange("projects", "github_url", e.target.value, index)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Live URL</label>
                            <input
                                type="url"
                                value={project.live_url}
                                onChange={(e) => handleInputChange("projects", "live_url", e.target.value, index)}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={project.description}
                            onChange={(e) => handleInputChange("projects", "description", e.target.value, index)}
                            rows="3"
                        />
                    </div>
                    <div className="form-group">
                        <label>Technologies</label>
                        {(Array.isArray(project.technologies) ? project.technologies : []).map((tech, techIndex) => (
                            <div key={techIndex} className="array-item">
                                <input
                                    type="text"
                                    value={tech}
                                    onChange={(e) => handleInputChange("projects", "technologies", e.target.value, index, techIndex)}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeArrayField("projects", index, "technologies", techIndex)}
                                    className="remove-btn"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => addArrayField("projects", index, "technologies", "")}
                            className="add-btn"
                        >
                            Add Technology
                        </button>
                    </div>
                    {project.features && (
                        <div className="form-group">
                            <label>Features</label>
                            {(Array.isArray(project.features) ? project.features : []).map((feature, featureIndex) => (
                                <div key={featureIndex} className="array-item">
                                    <input
                                        type="text"
                                        value={feature}
                                        onChange={(e) => handleInputChange("projects", "features", e.target.value, index, featureIndex)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeArrayField("projects", index, "features", featureIndex)}
                                        className="remove-btn"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addArrayField("projects", index, "features", "")}
                                className="add-btn"
                            >
                                Add Feature
                            </button>
                        </div>
                    )}
                </div>
            ))}
            <button
                type="button"
                onClick={() => addArrayItem("projects", {
                    name: "",
                    dates: "",
                    description: "",
                    technologies: [""],
                    github_url: "",
                    live_url: "",
                    features: [""]
                })}
                className="add-section-btn"
            >
                Add Project
            </button>
        </div>
    )
    const skillssection = () => (
        <div className='form-section'>
            <h1>Skills</h1>
            {/* Technical Skills */}
            <div className="skills-category">
                <h4>🔧 Technical Skills</h4>
                <div className="form-group">
                    <label>Technical Skills</label>
                    {(profileData.skills?.technical || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "technical", e.target.value, index)}
                                placeholder="e.g., Machine Learning, Data Science, Cloud Computing"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("technical", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("technical", "")}
                        className="add-btn"
                    >
                        Add Technical Skill
                    </button>
                </div>
            </div>

            {/* Programming Languages */}
            <div className="skills-category">
                <h4>💻 Programming Languages</h4>
                <div className="form-group">
                    <label>Programming Languages</label>
                    {(profileData.skills?.programming_languages || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "programming_languages", e.target.value, index)}
                                placeholder="e.g., Python, JavaScript, Java, C++"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("programming_languages", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("programming_languages", "")}
                        className="add-btn"
                    >
                        Add Programming Language
                    </button>
                </div>
            </div>

            {/* Frameworks & Libraries */}
            <div className="skills-category">
                <h4>⚡ Frameworks & Libraries</h4>
                <div className="form-group">
                    <label>Frameworks & Libraries</label>
                    {(profileData.skills?.frameworks || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "frameworks", e.target.value, index)}
                                placeholder="e.g., React, Django, TensorFlow, Spring"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("frameworks", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("frameworks", "")}
                        className="add-btn"
                    >
                        Add Framework
                    </button>
                </div>
            </div>

            {/* Tools & Technologies */}
            <div className="skills-category">
                <h4>Tools & Technologies</h4>
                <div className="form-group">
                    <label>Tools & Technologies</label>
                    {(profileData.skills?.tools || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "tools", e.target.value, index)}
                                placeholder="e.g., AWS, Docker, Git, PostgreSQL"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("tools", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("tools", "")}
                        className="add-btn"
                    >
                        Add Tool
                    </button>
                </div>
            </div>

            {/* Soft Skills */}
            <div className="skills-category">
                <h4>🤝 Soft Skills</h4>
                <div className="form-group">
                    <label>Soft Skills</label>
                    {(profileData.skills?.soft_skills || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "soft_skills", e.target.value, index)}
                                placeholder="e.g., Leadership, Communication, Problem Solving"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("soft_skills", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("soft_skills", "")}
                        className="add-btn"
                    >
                        Add Soft Skill
                    </button>
                </div>
            </div>

            {/* Languages */}
            <div className="skills-category">
                <h4>🌍 Languages</h4>
                <div className="form-group">
                    <label>Languages</label>
                    {(profileData.skills?.languages || []).map((skill, index) => (
                        <div key={index} className="array-item">
                            <input
                                type="text"
                                value={skill}
                                onChange={(e) => handleInputChange("skills", "languages", e.target.value, index)}
                                placeholder="e.g., English (Native), Spanish (Fluent)"
                            />
                            <button
                                type="button"
                                onClick={() => removeSkillFromCategory("languages", index)}
                                className="remove-btn"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => addSkillToCategory("languages", "")}
                        className="add-btn"
                    >
                        Add Language
                    </button>
                </div>
            </div>
        </div>
    )
    const preferencesection = () => (
        <div className='form-section'>
            <h1>Job Preferences</h1>
            <div className="form-grid">
                <div className="form-group">
                    <label>Veteran Status</label>
                    <select
                        value={profileData["veteran status"]}
                        onChange={(e) => handleInputChange("veteran status", null, e.target.value)}
                    >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Visa Status</label>
                    <input
                        type="text"
                        value={profileData["visa status"]}
                        onChange={(e) => handleInputChange("visa status", null, e.target.value)}
                        placeholder="e.g., F-1, H-1B, Green Card, Citizen"
                    />
                </div>
                <div className="form-group">
                    <label>Visa Sponsorship Required</label>
                    <select
                        value={profileData["visa sponsorship"]}
                        onChange={(e) => handleInputChange("visa sponsorship", null, e.target.value)}
                    >
                        <option value="">Select</option>
                        <option value="Required">Required</option>
                        <option value="Not Required">Not Required</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Willing to Relocate</label>
                    <select
                        value={profileData["willing to relocate"]}
                        onChange={(e) => handleInputChange("willing to relocate", null, e.target.value)}
                    >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                </div>
            </div>
            <div className="form-group">
                <label>Preferred Locations</label>
                {(Array.isArray(profileData["preferred location"]) ? profileData["preferred location"] : []).map((location, index) => (
                    <div key={index} className="array-item">
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => handleInputChange("preferred location", null, e.target.value, index)}
                            placeholder="e.g. Remote, New York NY, San Francisco CA"
                        />
                        <button
                            type="button"
                            onClick={() => removeArrayItem("preferred location", index)}
                            className="remove-btn"
                        >
                            Remove
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => addArrayItem("preferred location", "")}
                    className="add-btn"
                >
                    Add Location
                </button>
            </div>
            <div className="form-group">
                <label>Disability Status</label>
                <p className="field-hint">Optional - used only to answer EEO compliance questions on job applications.</p>
                {(Array.isArray(profileData["disabilities"]) ? profileData["disabilities"] : []).map((item, index) => (
                    <div key={index} className="array-item">
                        <input
                            type="text"
                            value={item}
                            onChange={(e) => handleInputChange("disabilities", null, e.target.value, index)}
                            placeholder="e.g. No disability, Blind, Deaf, Hard of hearing"
                        />
                        <button
                            type="button"
                            onClick={() => removeArrayItem("disabilities", index)}
                            className="remove-btn"
                        >
                            Remove
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => addArrayItem("disabilities", "")}
                    className="add-btn"
                >
                    Add Status
                </button>
            </div>
        </div>
    )

    const accountsettingssection = () => (
        <div className='form-section'>
            <h1>Account Settings</h1>
            <p className="section-subtitle">Manage your account security and data (GDPR compliant)</p>

            {/* Change Password */}
            <div className="settings-card">
                <h3><img className="settings-card-icon" src="/assets/icon-lock.png" alt="" /> Change Password</h3>
                <p>Update your account password for better security</p>
                {!showChangePassword ? (
                    <button
                        onClick={() => setShowChangePassword(true)}
                        className="settings-btn"
                    >
                        Change Password
                    </button>
                ) : (
                    <div className="settings-form">
                        <div className="form-group">
                            <label>Current Password</label>
                            <input
                                type="password"
                                value={passwordData.current_password}
                                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                                placeholder="Enter current password"
                            />
                        </div>
                        <div className="form-group">
                            <label>New Password</label>
                            <input
                                type="password"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                                placeholder="Enter new password (min 8 characters)"
                            />
                        </div>
                        <div className="form-group">
                            <label>Confirm New Password</label>
                            <input
                                type="password"
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                                placeholder="Re-enter new password"
                            />
                        </div>
                        <div className="settings-actions">
                            <button
                                onClick={handleChangePassword}
                                disabled={isChangingPassword || !passwordData.current_password || !passwordData.new_password}
                                className="save-btn"
                            >
                                {isChangingPassword ? 'Changing...' : 'Change Password'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowChangePassword(false);
                                    setPasswordData({current_password: '', new_password: '', confirm_password: ''});
                                }}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Export Data */}
            <div className="settings-card">
                <h3><img className="settings-card-icon" src="/assets/icon-up-loading.png" alt="" style={{transform:'scaleY(-1)'}} /> Download Your Data</h3>
                <p>Export all your data in JSON format (GDPR Right to Data Portability)</p>
                <button
                    onClick={handleExportData}
                    className="settings-btn export-btn"
                >
                    Export My Data
                </button>
            </div>

            {/* Delete Account */}
            <div className="settings-card danger-card">
                <h3><img className="settings-card-icon" src="/assets/icon-disabled.png" alt="" /> Delete Account</h3>
                <p>Permanently delete your account and all associated data (GDPR Right to Erasure)</p>
                {!showDeleteAccount ? (
                    <button
                        onClick={() => setShowDeleteAccount(true)}
                        className="settings-btn danger-btn"
                    >
                        Delete My Account
                    </button>
                ) : (
                    <div className="settings-form danger-form">
                        <div className="warning-box">
                            <strong>⚠️ WARNING: This action cannot be undone!</strong>
                            <p>All your data will be permanently deleted, including:</p>
                            <ul>
                                <li>Profile information</li>
                                <li>Resume data</li>
                                <li>Account history</li>
                                <li>All saved preferences</li>
                            </ul>
                        </div>
                        <div className="form-group">
                            <label>Enter Your Password</label>
                            <input
                                type="password"
                                value={deleteData.password}
                                onChange={(e) => setDeleteData({...deleteData, password: e.target.value})}
                                placeholder="Confirm your password"
                            />
                        </div>
                        <div className="form-group">
                            <label>Type DELETE to confirm</label>
                            <input
                                type="text"
                                value={deleteData.confirmation}
                                onChange={(e) => setDeleteData({...deleteData, confirmation: e.target.value})}
                                placeholder="Type DELETE in capital letters"
                            />
                        </div>
                        <div className="settings-actions">
                            <button
                                onClick={handleDeleteAccount}
                                disabled={isDeletingAccount || !deleteData.password || deleteData.confirmation !== 'DELETE'}
                                className="danger-btn"
                            >
                                {isDeletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteAccount(false);
                                    setDeleteData({password: '', confirmation: ''});
                                }}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const saveAiKeySettings = async () => {
        setAiError('');
        if (!aiPrimaryMode) { setAiError('Please select a primary mode.'); return; }
        if (aiSecondaryMode && aiSecondaryMode === aiPrimaryMode) {
            setAiError('Primary and secondary modes cannot be the same.'); return;
        }
        const needsCustomKey = aiPrimaryMode === 'custom' || aiSecondaryMode === 'custom';
        if (needsCustomKey && !aiCustomKey && !aiHasCustomKey) {
            setAiError('Please enter your Gemini API key.'); return;
        }

        setAiSaving(true);
        try {
            const payload = {
                primary_mode: aiPrimaryMode,
                secondary_mode: aiSecondaryMode || null,
            };
            if (aiCustomKey) payload.custom_api_key = aiCustomKey;

            const resp = await apiClient.post('/api/settings/ai-keys', payload);
            if (resp.data.success) {
                setAiSaved(true);
                setAiHasCustomKey(resp.data.has_custom_key);
                setAiCustomKey('');  // clear plaintext from state
                if (resp.data.has_custom_key) setAiMaskedKey('(saved)');
                setShowAiSetupModal(false);   // dismiss modal on successful save
                setTimeout(() => setAiSaved(false), 3000);
            } else {
                setAiError(resp.data.error || 'Failed to save settings.');
            }
        } catch (err) {
            setAiError(err.response?.data?.error || 'Failed to save settings.');
        } finally {
            setAiSaving(false);
        }
    };

    const aiSetupModal = () => {
        if (!showAiSetupModal) return null;
        const needsKey = aiPrimaryMode === 'custom' || aiSecondaryMode === 'custom';
        return (
            <div className="ai-modal-overlay">
                <div className="ai-modal">
                    <h2>⚙️ Set Up Your AI Engine</h2>
                    <p className="ai-modal-subtitle">
                        Choose how Launchway makes AI calls. You can change this any time
                        in <strong>AI Engine</strong> settings.
                    </p>

                    <div className="ai-modal-block">
                        <label className="ai-modal-label">Primary method</label>
                        <div className="ai-mode-selector">
                            <label className={`ai-mode-card ${aiPrimaryMode === 'launchway' ? 'selected' : ''}`}>
                                <input type="radio" name="modal-primary" value="launchway"
                                    checked={aiPrimaryMode === 'launchway'}
                                    onChange={() => setAiPrimaryMode('launchway')} />
                                <img className="ai-mode-icon" src="/assets/icon-global.png" alt="" />
                                <span className="ai-mode-title">Launchway's Shared Keys</span>
                                <span className="ai-mode-desc">Free. May be slower during peak usage.</span>
                            </label>
                            <label className={`ai-mode-card ${aiPrimaryMode === 'custom' ? 'selected' : ''}`}>
                                <input type="radio" name="modal-primary" value="custom"
                                    checked={aiPrimaryMode === 'custom'}
                                    onChange={() => setAiPrimaryMode('custom')} />
                                <img className="ai-mode-icon" src="/assets/icon-key.png" alt="" />
                                <span className="ai-mode-title">My Own Gemini Key</span>
                                <span className="ai-mode-desc">Faster, private quota.</span>
                            </label>
                        </div>
                    </div>

                    <div className="ai-modal-block">
                        <label className="ai-modal-label">
                            Fallback method&nbsp;<span style={{color:'rgba(255,255,255,0.4)', fontWeight:400}}>(optional)</span>
                        </label>
                        <div className="ai-mode-selector">
                            <label className={`ai-mode-card ${aiSecondaryMode === '' ? 'selected' : ''}`}>
                                <input type="radio" name="modal-secondary" value=""
                                    checked={aiSecondaryMode === ''}
                                    onChange={() => setAiSecondaryMode('')} />
                                <img className="ai-mode-icon" src="/assets/icon-disabled.png" alt="" />
                                <span className="ai-mode-title">None</span>
                                <span className="ai-mode-desc">Stop when primary runs out.</span>
                            </label>
                            {aiPrimaryMode !== 'launchway' && (
                                <label className={`ai-mode-card ${aiSecondaryMode === 'launchway' ? 'selected' : ''}`}>
                                    <input type="radio" name="modal-secondary" value="launchway"
                                        checked={aiSecondaryMode === 'launchway'}
                                        onChange={() => setAiSecondaryMode('launchway')} />
                                    <img className="ai-mode-icon" src="/assets/icon-global.png" alt="" />
                                    <span className="ai-mode-title">Launchway's Shared Keys</span>
                                    <span className="ai-mode-desc">Fallback to shared keys.</span>
                                </label>
                            )}
                            {aiPrimaryMode !== 'custom' && (
                                <label className={`ai-mode-card ${aiSecondaryMode === 'custom' ? 'selected' : ''}`}>
                                    <input type="radio" name="modal-secondary" value="custom"
                                        checked={aiSecondaryMode === 'custom'}
                                        onChange={() => setAiSecondaryMode('custom')} />
                                    <img className="ai-mode-icon" src="/assets/icon-key.png" alt="" />
                                    <span className="ai-mode-title">My Own Gemini Key</span>
                                    <span className="ai-mode-desc">Fallback to your key.</span>
                                </label>
                            )}
                        </div>
                    </div>

                    {needsKey && (
                        <div className="ai-modal-block">
                            <label className="ai-modal-label">
                                Your Gemini API Key&nbsp;
                                <a href="https://aistudio.google.com/app/apikey" target="_blank"
                                   rel="noopener noreferrer" style={{color:'#81C784', fontWeight:400, fontSize:'0.85rem'}}>
                                    Get one free →
                                </a>
                            </label>
                            <input
                                type="password"
                                placeholder="AIza…"
                                value={aiCustomKey}
                                onChange={e => setAiCustomKey(e.target.value)}
                                className="resume-url-input"
                                autoComplete="off"
                                style={{fontFamily:'monospace'}}
                            />
                        </div>
                    )}

                    {aiError && (
                        <p style={{color:'#FF6B6B', fontSize:'0.88rem', margin:'8px 0 0'}}>{aiError}</p>
                    )}

                    <div className="ai-modal-actions">
                        <button
                            onClick={saveAiKeySettings}
                            disabled={aiSaving}
                            className="process-btn valid"
                            style={{flex:1}}
                        >
                            {aiSaving ? 'Saving…' : 'Save & Continue'}
                        </button>
                        <button
                            onClick={() => {
                                // User dismissed - set Launchway as default silently
                                setAiPrimaryMode('launchway');
                                setShowAiSetupModal(false);
                                apiClient.post('/api/settings/ai-keys', { primary_mode: 'launchway', secondary_mode: null })
                                    .catch(() => {});
                            }}
                            className="disconnect-btn-small"
                            style={{padding:'10px 18px'}}
                        >
                            Use Launchway keys for now
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const aienginesection = () => {
        const needsKey = aiPrimaryMode === 'custom' || aiSecondaryMode === 'custom';

        return (
            <div className="form-section">
                <h1>AI Engine</h1>
                <p className="section-subtitle">
                    Choose how the agent makes Gemini API calls. You can configure a primary
                    method and an optional fallback - if one runs out of quota, the other takes over.
                </p>

                {/* Primary */}
                <div className="ai-engine-block">
                    <h3>Primary Method <span style={{color:'#81C784'}}>✦ Required</span></h3>
                    <div className="ai-mode-selector">
                        <label className={`ai-mode-card ${aiPrimaryMode === 'launchway' ? 'selected' : ''}`}>
                            <input type="radio" name="primary" value="launchway"
                                checked={aiPrimaryMode === 'launchway'}
                                onChange={() => setAiPrimaryMode('launchway')} />
                            <img className="ai-mode-icon" src="/assets/icon-global.png" alt="" />
                            <span className="ai-mode-title">Launchway's Shared Keys</span>
                            <span className="ai-mode-desc">Free to use. May be slower when many users are active.</span>
                        </label>
                        <label className={`ai-mode-card ${aiPrimaryMode === 'custom' ? 'selected' : ''}`}>
                            <input type="radio" name="primary" value="custom"
                                checked={aiPrimaryMode === 'custom'}
                                onChange={() => setAiPrimaryMode('custom')} />
                            <img className="ai-mode-icon" src="/assets/icon-key.png" alt="" />
                            <span className="ai-mode-title">My Own Gemini Key</span>
                            <span className="ai-mode-desc">Faster, private quota. Requires a Google AI Studio API key.</span>
                        </label>
                    </div>
                    {aiPrimaryMode === 'launchway' && (
                        <div className="info-note" style={{borderLeft:'4px solid #FFB74D', marginTop:'8px'}}>
                            ⚠️ Shared keys are rate-limited. During peak hours responses may be slower.
                            Consider adding your own key as a fallback.
                        </div>
                    )}
                </div>

                {/* Secondary (optional) */}
                <div className="ai-engine-block" style={{marginTop:'20px'}}>
                    <h3>Secondary Method (Fallback) <span style={{color:'rgba(255,255,255,0.5)', fontSize:'0.85rem'}}>Optional</span></h3>
                    <p style={{fontSize:'0.88rem', color:'rgba(255,255,255,0.65)', marginBottom:'10px'}}>
                        If your primary method hits its quota, the agent automatically switches here.
                    </p>
                    <div className="ai-mode-selector">
                        <label className={`ai-mode-card ${aiSecondaryMode === '' ? 'selected' : ''}`}>
                            <input type="radio" name="secondary" value=""
                                checked={aiSecondaryMode === ''}
                                onChange={() => setAiSecondaryMode('')} />
                            <img className="ai-mode-icon" src="/assets/icon-disabled.png" alt="" />
                            <span className="ai-mode-title">No fallback</span>
                            <span className="ai-mode-desc">Stop when primary quota is exhausted.</span>
                        </label>
                        {aiPrimaryMode !== 'launchway' && (
                            <label className={`ai-mode-card ${aiSecondaryMode === 'launchway' ? 'selected' : ''}`}>
                                <input type="radio" name="secondary" value="launchway"
                                    checked={aiSecondaryMode === 'launchway'}
                                    onChange={() => setAiSecondaryMode('launchway')} />
                                <img className="ai-mode-icon" src="/assets/icon-global.png" alt="" />
                                <span className="ai-mode-title">Launchway's Shared Keys</span>
                                <span className="ai-mode-desc">Fall back to shared keys when your key is exhausted.</span>
                            </label>
                        )}
                        {aiPrimaryMode !== 'custom' && (
                            <label className={`ai-mode-card ${aiSecondaryMode === 'custom' ? 'selected' : ''}`}>
                                <input type="radio" name="secondary" value="custom"
                                    checked={aiSecondaryMode === 'custom'}
                                    onChange={() => setAiSecondaryMode('custom')} />
                                <img className="ai-mode-icon" src="/assets/icon-key.png" alt="" />
                                <span className="ai-mode-title">My Own Gemini Key</span>
                                <span className="ai-mode-desc">Fall back to your own key when shared quota runs out.</span>
                            </label>
                        )}
                    </div>
                </div>

                {/* API Key input */}
                {needsKey && (
                    <div className="ai-engine-block" style={{marginTop:'20px'}}>
                        <h3>Your Gemini API Key</h3>
                        <p style={{fontSize:'0.88rem', color:'rgba(255,255,255,0.65)', marginBottom:'8px'}}>
                            Get a free key at{' '}
                            <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer"
                               style={{color:'#81C784'}}>aistudio.google.com</a>.
                            Your key is encrypted before storage - we never see it in plaintext.
                        </p>
                        {aiHasCustomKey && !aiCustomKey && (
                            <div className="info-note" style={{borderLeft:'4px solid #81C784', marginBottom:'8px'}}>
                                ✅ A key is already saved: <code>{aiMaskedKey}</code>. Leave blank to keep it unchanged.
                            </div>
                        )}
                        <input
                            type="password"
                            placeholder="AIza…"
                            value={aiCustomKey}
                            onChange={e => setAiCustomKey(e.target.value)}
                            className="resume-url-input"
                            autoComplete="off"
                            style={{fontFamily:'monospace'}}
                        />
                    </div>
                )}

                {/* Retry / cooldown info */}
                <div className="info-note" style={{marginTop:'16px'}}>
                    <strong>How the fallback works:</strong> Primary → Secondary → retry Primary →
                    60-second cooldown → final retry. If everything fails, the agent pauses and
                    notifies you to try again later.
                </div>

                {aiError && (
                    <div style={{color:'#FF6B6B', marginTop:'10px', fontSize:'0.9rem'}}>
                        ⚠️ {aiError}
                    </div>
                )}

                <button
                    onClick={saveAiKeySettings}
                    disabled={aiSaving}
                    className="save-cover-letter-btn"
                    style={{marginTop:'18px'}}
                >
                    {aiSaving ? 'Saving…' : aiSaved ? '✓ Saved!' : 'Save AI Engine Settings'}
                </button>
            </div>
        );
    };

    const sections = [
        { id: 'resume', label: 'Resume Upload', iconPath: '/assets/icon-resume.png', locked: false },
        { id: 'personaldetails', label: 'Personal Info', iconPath: '/assets/icon-user.png', locked: !resumeUploadAttempted },
        { id: 'education', label: 'Education', iconPath: '/assets/icon-education.png', locked: !resumeUploadAttempted },
        { id: 'workexperience', label: 'Experience', iconPath: '/assets/icon-experience.png', locked: !resumeUploadAttempted },
        { id: 'projects', label: 'Projects', iconPath: '/assets/icon-project.png', locked: !resumeUploadAttempted },
        { id: 'skills', label: 'Skills', iconPath: '/assets/icon-skills.png', locked: !resumeUploadAttempted },
        { id: 'preferences', label: 'Preferences', iconPath: '/assets/icon-preferences.png', locked: !resumeUploadAttempted },
        { id: 'aiengine', label: 'AI Engine', iconPath: '/assets/icon-ai-engine.png', locked: false },
        { id: 'accountsettings', label: 'Account Settings', iconPath: '/assets/icon-account-setting.png', locked: false }
    ];

    const renderSection = () => {
        switch (activeSection) {
            case 'resume': return resumeuploadsection();
            case 'personaldetails': return personaldetailssection();
            case 'education': return educationsection();
            case 'workexperience': return workexperiencesection();
            case 'projects': return projectssection();
            case 'skills': return skillssection();
            case 'preferences': return preferencesection();
            case 'aiengine': return aienginesection();
            case 'accountsettings': return accountsettingssection();
            default: return resumeuploadsection();
        }
    }
    const emailChangeModal = () => {
        if (!showEmailChangeModal) return null;
        return (
            <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999, padding: '20px', backdropFilter: 'blur(6px)'
            }}>
                <div style={{
                    background: 'linear-gradient(145deg, #0f1729, #131d35)',
                    border: '1.5px solid rgba(255,255,255,0.08)',
                    borderRadius: '14px', padding: '32px',
                    width: '100%', maxWidth: '460px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(6,182,212,0.06)'
                }}>
                    <h2 style={{ margin: '0 0 8px', color: '#f0f4f8', fontSize: '20px', fontFamily: "'Space Mono', monospace" }}>Change Email Address</h2>
                    <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.55)', fontSize: '14px' }}>
                        Current email: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{profileData.email}</strong>
                    </p>

                    {emailChangeSuccess ? (
                        <div>
                            <div style={{
                                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)',
                                borderRadius: '8px', padding: '14px 16px',
                                color: '#6ee7b7', fontSize: '14px', marginBottom: '20px'
                            }}>
                                {emailChangeSuccess}
                            </div>
                            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                                You can close this dialog. Your current email stays active until you click the verification link.
                            </p>
                            <button
                                onClick={() => { setShowEmailChangeModal(false); setEmailChangeSuccess(''); }}
                                style={{
                                    marginTop: '16px', width: '100%', padding: '12px',
                                    background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                                    color: '#fff', border: 'none', borderRadius: '8px',
                                    fontWeight: '700', fontSize: '15px', cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div style={{
                                background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.25)',
                                borderRadius: '8px', padding: '12px 16px',
                                fontSize: '13px', color: 'rgba(255,255,255,0.65)', marginBottom: '20px',
                                lineHeight: '1.5'
                            }}>
                                <strong style={{ color: '#67e8f9' }}>How it works:</strong> We'll send a verification link to your new email.
                            Your account email won't change until you click that link - so you'll never get locked out.
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: 'rgba(255,255,255,0.7)' }}>
                                    New Email Address
                                </label>
                                <input
                                    type="email"
                                    value={emailChangeData.new_email}
                                    onChange={(e) => setEmailChangeData(p => ({ ...p, new_email: e.target.value }))}
                                    placeholder="you@example.com"
                                    style={{
                                        width: '100%', padding: '10px 12px', fontSize: '14px',
                                        background: 'rgba(255,255,255,0.05)', color: '#f0f4f8',
                                        border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px',
                                        boxSizing: 'border-box', outline: 'none'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: 'rgba(255,255,255,0.7)' }}>
                                    Confirm New Email Address
                                </label>
                                <input
                                    type="email"
                                    value={emailChangeData.confirm_email}
                                    onChange={(e) => setEmailChangeData(p => ({ ...p, confirm_email: e.target.value }))}
                                    placeholder="you@example.com"
                                    style={{
                                        width: '100%', padding: '10px 12px', fontSize: '14px',
                                        background: 'rgba(255,255,255,0.05)', color: '#f0f4f8',
                                        border: '1px solid rgba(255,255,255,0.12)', borderRadius: '7px',
                                        boxSizing: 'border-box', outline: 'none'
                                    }}
                                />
                            </div>

                            {emailChangeError && (
                                <div style={{
                                    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
                                    borderRadius: '7px', padding: '10px 14px',
                                    color: '#fca5a5', fontSize: '13px', marginBottom: '14px'
                                }}>
                                    {emailChangeError}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                                <button
                                    onClick={() => setShowEmailChangeModal(false)}
                                    disabled={emailChangeLoading}
                                    style={{
                                        flex: 1, padding: '12px',
                                        background: 'rgba(255,255,255,0.06)',
                                        color: 'rgba(255,255,255,0.7)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px',
                                        fontWeight: '600', fontSize: '14px', cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEmailChangeRequest}
                                    disabled={emailChangeLoading}
                                    style={{
                                        flex: 2, padding: '12px',
                                        background: emailChangeLoading
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'linear-gradient(135deg, #06b6d4, #0891b2)',
                                        color: '#fff', border: 'none', borderRadius: '8px',
                                        fontWeight: '700', fontSize: '14px',
                                        cursor: emailChangeLoading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {emailChangeLoading ? 'Sending...' : 'Send Verification Email'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className='profile-container'>
            {aiSetupModal()}
            {emailChangeModal()}
            <div className='profile-header'>
                <button onClick={() => navigate('/')} className="back-btn">← Back to Home</button>
                <div className="profile-header__right">
                    <span className={`autosave-indicator autosave-indicator--${autoSaveStatus}`}>
                        {autoSaveStatus === 'saving' && 'Saving...'}
                        {autoSaveStatus === 'saved' && 'All changes saved'}
                        {autoSaveStatus === 'error' && 'Save failed'}
                    </span>
                    <button onClick={saveProfile} className="save-btn">
                        Save Profile
                    </button>
                </div>
            </div>

            <div className='profile-body'>
                <div className='sidebar'>
                    <nav className='sidebar-nav'>
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => !section.locked && setActiveSection(section.id)}
                                className={`sidebar-item ${activeSection === section.id ? 'active' : ''} ${section.locked ? 'locked' : ''}`}
                                disabled={section.locked}
                                title={section.locked ? 'Please upload your resume first. You can edit manually even if parsing fails.' : ''}
                            >
                                <img className='sidebar-icon' src={section.locked ? '/assets/icon-lock.png' : section.iconPath} alt="" />
                                <span className='sidebar-label'>{section.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                <div className='main-content'>
                    {renderSection()}
                </div>
            </div>

            {/* Toast notifications */}
            {toasts.length > 0 && (
                <div className="toast-container">
                    {toasts.map(toast => (
                        <div key={toast.id} className={`toast toast--${toast.type}`}>
                            <span className="toast__icon">
                                {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✗' : 'i'}
                            </span>
                            <span className="toast__message">{toast.message}</span>
                            <button className="toast__close" onClick={() => removeToast(toast.id)}>&times;</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Profile;